<template>
<div>

  <nav class="flex flex-wrap items-center text-sm mb-3">
    <router-link :to="'/admin/validations/'"
      class="flex items-center text-blue hover:text-blue-darker hover:underline">
      <i class="fas fa-check-double mr-1"></i>{{ $t('validations') }}
    </router-link>

    <i class="fas fa-chevron-right text-grey text-xs mx-2"></i>

    <span class="flex items-center rounded-full bg-blue-lightest border border-blue-lighter text-blue-darker font-bold px-3 py-0.5 truncate"
      style="max-width:24rem"
      :title="form.code + ' ' + form.name">
      <i class="fas fa-project-diagram mr-1"></i>
      <span class="text-blue-dark mr-1">{{ form.code }}</span>{{ form.name }}
    </span>
  </nav>


  <div class="panel">

    <div class="flex">
      <div class="w-1/2 pr-4">
        <l-form-input ref="templateCode"
          :form="form"
          field="code"
          label="code"
        />
      </div>
      <div class="w-1/2 pr-4">
        <label for="flow_type" class="label">{{ $t('select_type') }}</label>
        <b-select ref="input"
          v-model="form.flow_type"
          :placeholder="$t('select_type')"
          id="flow_type"
          name="flow_type"
          class="w-full"
          required
          expanded
        >
          <option v-for="(type, index) in templateTypes" :value="index" :key="index">
            {{ type }}
          </option>
        </b-select>
      </div>
      <div class="w-full pr-4">
        <l-form-input ref="templateName"
          :form="form"
          field="name"
          label="name"
        />
      </div>
    </div>


    <!-- Descripción -->
    <div class="flex items-center mt-8">
      <l-form-input
        :form="form"
        field="description"
        label="description"
        input-type="textarea"
        :maxlength="2000"
        class="w-full"
      />
    </div>

    <div class="flex items-center mt-8">
      <b-tooltip :label="$t('validation_flow_preserve_validated_groups_label')" position="is-right">
        <b-icon icon="info" pack="fas" size="is-small" class="ml-1 float-right" />
      </b-tooltip>
      
     <b-field :label="$t('preserve_validated_groups')">
      <b-switch
        v-model="form.preserve_validated_groups"
        :type="'is-success'"
        :passive-type="'is-danger'"
      >
      </b-switch>
    </b-field>
    </div>
    <!-- documentos heredables entre tareas -->
    <div class="flex items-center mt-8">
      <b-tooltip :label="$t('validation_flow_inherit_docs_label')" position="is-right">
        <b-icon icon="info" pack="fas" size="is-small" class="ml-1 float-right"/>
      </b-tooltip>

      <b-field :label="$t('inherit_docs')">
        <b-switch
          v-model="form.inherit_docs"
          :type="'is-success'"
          :passive-type="'is-danger'"
        >
        </b-switch>
      </b-field>

      <label class="text-red text-xs">* {{ $t('validation_flow_inherit_change_warning') }} </label>
    </div>


    <!-- Tareas -->
    <div class="mt-8">
      <label><b>{{ $t('tasks') }}</b></label>

      <!-- tareas padre ordenables con drag and drop (actualiza order_column) -->
      <draggable
        v-model="form.tasks"
        handle=".task-drag-handle"
        :animation="200"
        :disabled="isSavingTasksOrder"
        tag="div"
        @end="reorderTasks"
      >
      <!-- recuadro de tarea -->
      <div class="w-full border border-blue-lighter rounded flex-col items-center p-2 bg-blue-lightest mb-1"
        v-for="(task, taskKey) in form.tasks" :key="task.id">

        <!-- div linea tarea padre -->
        <div>
          <!-- drag handle -->
          <b-tooltip :label="$t('order')" position="is-right">
              <div class="flex text-gray-500 cursor-move task-drag-handle">
                <b-icon pack="fas" icon="grip-vertical"/>
              </div>
          </b-tooltip>
          <!-- delete -->
          <b-tooltip :label="$t('remove')" position="is-left" type="is-danger">
              <div @click="unlinkTask(task.id, task.code + ' ' +task.name)" class="flex cursor-pointer text-red">
                <b-icon pack="fas" icon="trash-alt"/>
              </div>
          </b-tooltip>
          <!-- save -->
          <b-tooltip v-if="tasksInEdition.includes(task.id)" :label="$t('save')" position="is-left">
              <div @click="sendTaskData(task.id, taskKey)" class="flex cursor-pointer text-blue">
                <b-icon pack="fas" icon="download"/>
              </div>
          </b-tooltip>
          <!-- edit -->
          <b-tooltip v-else :label="$t('edit')" position="is-left">
              <div @click="editTask(task.id)" class="flex cursor-pointer text-blue">
                <b-icon pack="fas" icon="pencil-alt"/>
              </div>
          </b-tooltip>

          <span v-if="tasksInEdition.includes(task.id)"><input v-model="form.tasks[taskKey].order_column" type="text" class="mr-2"></span>
          <span v-else class="ml-2 italic">({{ task.order_column }})</span>

          <span v-if="tasksInEdition.includes(task.id)"><input v-model="form.tasks[taskKey].code" type="text" class="mr-2"></span>
          <span v-else class="ml-2 font-bold">{{ task.code }}</span>

          <span v-if="tasksInEdition.includes(task.id)"><input v-model="form.tasks[taskKey].name" type="text" class="mr-2"></span>
          <span v-else class="ml-2">{{ task.name }}</span>

          <span v-if="tasksInEdition.includes(task.id)"><input v-model="form.tasks[taskKey].description" type="text" class="mr-2" :placeholder="$t('description')"></span>
          <span v-else class="ml-2">{{ task.description }}</span>
        </div>

        <!-- div linea tarea hija -->
        <div class="bg-gray-100" v-for="subTask in task.children" :key="subTask.id">

          <b-icon pack="fas" icon="chevron-right"/>
          <!-- delete -->
          <b-tooltip :label="$t('remove')" position="is-left" type="is-danger">
              <div @click="unlinkTask(subTask.id, subTask.code + ' ' +subTask.name)" class="flex cursor-pointer text-red">
                <b-icon pack="fas" icon="trash-alt"/>
              </div>
          </b-tooltip>
          <!-- edit -->
          <b-tooltip :label="$t('edit')" position="is-left">
              <div @click="editTask(subTask.id)" class="flex cursor-pointer text-blue">
                <b-icon pack="fas" icon="pencil-alt"/>
              </div>
          </b-tooltip>
          <i>({{ subTask.order_column }})</i>
          <span class="ml-2 font-bold">{{ subTask.code }}</span>
          <span class="ml-2">{{ subTask.name }}</span>
          <span class="ml-2">{{ task.description }}</span>

        </div>

      </div>
      </draggable>

      <!-- recuadro añadir tarea -->
      <div class="w-full border border-blue-lighter rounded flex items-center p-2 bg-blue-lightest mb-1">

        <b-tooltip :label="$t('add_task')" position="is-left">
            <button class="btn btn-green items-center mr-2" type="button" :disabled="isCreatingTask" @click="linkTask()">
              <i :class="isCreatingTask ? 'fas fa-spinner fa-spin' : 'fas fa-plus'"></i>
            </button>
        </b-tooltip>

        <b-tooltip :label="$t('order')" position="is-left">
          <input v-model.number="newTask.order_column" type="number" min="0" step="1" :placeholder="$t('order')" class="input mr-2" style="width:60px" @keyup.enter="linkTask()">
        </b-tooltip>

        <b-tooltip :label="$t('code')" position="is-left">
          <input v-model="newTask.code" type="text" :placeholder="$t('code')" class="input mr-2" style="width:140px" @keyup.enter="linkTask()">
        </b-tooltip>

        <b-tooltip :label="$t('name')" position="is-left">
          <input ref="newTaskName" v-model="newTask.name" type="text" :placeholder="$t('name')" class="input mr-2" @keyup.enter="linkTask()">
        </b-tooltip>

        <b-tooltip :label="$t('description')" position="is-left">
          <input v-model="newTask.description" type="text" :placeholder="$t('description')" class="input mr-2" @keyup.enter="linkTask()">
        </b-tooltip>

        <b-tooltip :label="$t('initial_group')" position="is-left">
          <b-select v-model="newTask.manager_group_id" :placeholder="$t('initial_group')" class="mr-2">
            <option v-for="group in templateGroups" :value="group.id" :key="'group_'+group.id">
              {{ group.code }}
            </option>
          </b-select>
        </b-tooltip>

        <b-tooltip :label="$t('parent_task')" position="is-left">
          <b-select v-model="newTask.parent_id" :placeholder="$t('select')" class="mr-2">
            <option :value="0">{{ $t('no_parent') }}</option>
            <option v-for="(task) in form.tasks" :value="task.id" :key="'parent_select_'+task.id">
              {{ task.code }} {{ task.name }}
            </option>
          </b-select>
        </b-tooltip>

        <b-tooltip :label="$t('concurrent_child_tasks')" position="is-left" v-show="!newTask.parent_id">
          <b-switch v-model="newTask.is_concurrent" type="is-success" passive-type="is-danger">
            {{ $t('concurrent') }}
          </b-switch>
        </b-tooltip>

      </div>

    </div>


    <!-- Grupos -->
    <div class="border-b border-grey-light pb-2 mt-8 mb-4">
      <h3 class="text-sm font-bold uppercase tracking-wide text-grey-darker">
        {{ $t('groups') }}
      </h3>
    </div>
    <div class="grid grid-cols-4 gap-4">

      <!-- recuadro grupo -->
      <div class="flex flex-col rounded-lg shadow-sm hover:shadow-md transition-shadow bg-blue-lightest border border-blue-lighter p-3"
        v-for="(group) in form.groups" :key="group.id">

        <!-- franja superior de color -->
        <div class="h-1 -mx-3 -mt-3 mb-3 rounded-t-lg bg-blue-light"></div>

        <div class="flex items-center">
          <i class="fas fa-users text-blue-light mr-2"></i>
          <span class="text-xl font-bold text-blue-darker truncate flex-grow">{{ group.code }}</span>

          <b-tooltip :label="$t('delete')" position="is-left" type="is-danger" class="ml-2">
            <span class="text-grey-dark hover:text-red cursor-pointer" @click="unlinkGroup(group.id, group.code)">
              <i class="fas fa-times-circle"></i>
            </span>
          </b-tooltip>
        </div>

        <div class="flex items-center border-t border-b border-blue-lighter py-2 mt-3 mb-3">
          <b-switch
            v-model="group.clone_docs"
            :type="'is-success'"
            :passive-type="'is-danger'"
            size="is-small"
          >
            <span class="text-sm text-grey-darker">
              <i class="fas fa-copy text-blue-light mr-1"></i>{{ $t('clone_docs') }}
            </span>
          </b-switch>
        </div>

        <userlist
          :ref="'group_' + group.id + '_participants'"
          title="users_participants"
          icon="users"
          api-url="/api/v2/validations/templategroup"
          :parent-entity-id="parseInt(group.id)"
          :users-permission-type="['participate']"
          :allow-create-users="false"
          @user-added="participantAddedInGroup($event)"
          :local-mode="false"
          :allow-company-users22="true"
          :box-full-width="true"
          :validation-users="true"
          roleName="participant" />
      </div>

      <!-- recuadro añadir grupo -->
      <div class="flex flex-col justify-center rounded-lg border-2 border-dashed border-blue-lighter bg-grey-lightest p-3 text-center">

        <span class="font-bold text-blue">{{ $t('add_group') }}</span>

        <b-select v-model="groupToAdd" :placeholder="$t('select')" expanded class="mt-2">
          <option v-for="group in templateGroups" :value="group" :key="'group_'+group.id">
            {{ group.code }}
          </option>
        </b-select>

        <button class="btn btn-green items-center mt-2" style="margin:auto" type="button" @click="linkGroup()">
          <i class="fas fa-plus mr-1"></i> {{ $t('add_group') }}
        </button>

      </div>

    </div>
    <div class="flex items-start rounded bg-red-lightest text-red-dark text-xs p-2 mt-4">
      <i class="fas fa-exclamation-triangle mr-2 mt-0.5"></i>
      <span>{{ $t('validation_flow_groups_change_warning') }}</span>
    </div>

  </div>

  <form @submit.prevent="sendTemplateData" @keydown="form.onKeydown($event)"
    class="w-full flex items-center justify-end mb-8">
    <v-button :disabled="disableSaveButton" type="submit" icon="thumbs-up" icon-pack="far" class="mr-4">{{ $t('save') }}</v-button>
    <v-button color="grey" icon="undo-alt" @click="$router.go(-1)">{{ $t('cancel') }}</v-button>
  </form>

</div>
</template>

<script>
import axios from 'axios'
import Form from 'vform'
import draggable from 'vuedraggable'
import { mapGetters } from 'vuex'
import Userlist from '~/components/UserBox/UserList'

export default {
  middleware: 'auth',
  title: 'validations_edit_page_title',
  subtitle: 'validations_edit_page_subtitle',
  menuOption: '0-5',

  components: {
    Userlist,
    draggable,
  },

  data: () => ({
    templateId: null,
    form: new Form({
      code: '',
      name: '',
      description: '',
      flow_type: 0,
      tasks: [],
      groups: [],
      inherit_docs: false,
      preserve_validated_groups: false,
    }),
    newTask: new Form({
      code: '',
      name: '',
      description: '',
      order_column: 0,
      manager_group_id: null, // null para que b-select muestre el placeholder
      parent_id: 0,
      is_concurrent: false,
    }),
    tasksInEdition: [], // tareas que estan en modo edicion. Editor inline en desuso (ahora redirige a su pantalla)
    disableSaveButton: false,
    isSavingTasksOrder: false, // bloquea el drag and drop mientras se guarda el nuevo orden
    isCreatingTask: false, // evita crear la tarea dos veces (doble click / enter)
    templateGroups: [], // plantillas de grupos posibles a añadir al template
    groupToAdd: {}, // el grupo elegido para añadir
    templateTypes: ['Estándar', 'Especificaciones Técnicas', 'Incidencias', 'Certificaciones'],
  }),

  created() {

    this.templateId = this.$route.params.validation_id

  },

  computed: {
    ...mapGetters({
      company: 'app/company',
    }),
  },

  watch: {
    // al cambiar la tarea padre, orden y código sugeridos pasan a ser los siguientes entre sus subtareas
    'newTask.parent_id'() {
      this.suggestNewTaskFields()
    },
  },

  mounted() {
    this.getGroups()
    this.getTemplateData()
    this.$refs.templateCode.setFocus()
  },

  methods: {

    // Listado de todas las plantillas de grupo (para selector al añadir)
    async getGroups () {
      const { data } = await axios.get("/api/v2/validations/templategroups", {'params' : { company_id: this.company.id }});

      if (data.groups) {
        this.templateGroups = data.groups;
      } else {
        // Error
        this.$notify.error('error_retrieving_data')

      }
    },

    async getTemplateData() {
      const { data } = await axios.get('/api/v2/validations/template/' + this.templateId)
      if (data && data.success) {

        this.form.code = data.data.code
        this.form.name = data.data.name
        this.form.description = data.data.description
        this.form.flow_type = data.data.flow_type
        this.form.tasks = data.data.tasks
        this.suggestNewTaskFields()
        // Convertir clone_docs de 1/0 a true/false para cada grupo
        this.form.groups = data.data.groups.map(group => ({
          ...group,
          clone_docs: group.clone_docs == 1 ? true : false
        }))
        this.form.inherit_docs = data.data.inherit_docs == 1 ? true : false
        this.form.preserve_validated_groups = data.data.preserve_validated_groups == 1 ? true : false

      } else if(data && data.msg){

        this.$notify.error('no_access_permissions')
        this.$router.push({ name: 'project.home', params: {} })

      } else {
        this.$notify.error('error_retrieving_data')
      }
    },


    async sendTemplateData() {
      var url = '/api/v2/validations/template/' + this.templateId;
      this.disableButton();

      this.form.inherit_docs = this.form.inherit_docs ? 1 : 0 // laravel espera 1 o 0
      this.form.preserve_validated_groups = this.form.preserve_validated_groups ? 1 : 0 // laravel espera 1 o 0
      this.form.groups = this.form.groups.map(group => ({
        ...group,
        clone_docs: group.clone_docs ? 1 : 0 // laravel espera 1 o 0
      }))

      // Enviamos el form
      const { data } = await this.form.post(url)
      if (data && data.success) {

        this.$notify.success('success_editing')
        this.$router.push({ name: 'admin.validations' })

      } else if(data && data.msg){

        this.$notify.error('no_access_permissions')
        this.$router.push({ name: 'project.home', params: {} })

      } else {
        this.$notify.error('error_saving')
      }
    },


    // Hermanas de la nueva tarea: tareas raíz, o las subtareas del padre elegido.
    siblingTasks(parentId) {
      if (!parentId) {
        return this.form.tasks || []
      }
      const parent = (this.form.tasks || []).find((task) => task.id === parentId)
      return parent && Array.isArray(parent.children) ? parent.children : []
    },

    // Rellena orden y código sugeridos para la nueva tarea. Ambos campos siguen siendo editables.
    suggestNewTaskFields() {
      this.newTask.order_column = this.nextTaskOrder(this.newTask.parent_id)
      this.newTask.code = this.nextTaskCode(this.newTask.parent_id)
    },

    // Orden sugerido: el mayor order_column de sus hermanas + 1 (0 si no hay ninguna).
    nextTaskOrder(parentId) {
      const siblings = this.siblingTasks(parentId)
      if (!siblings.length) {
        return 0
      }

      return Math.max(...siblings.map((task) => Number(task.order_column) || 0)) + 1
    },

    // Código sugerido a partir de la última hermana (mayor order_column):
    // si termina en número lo incrementa respetando ceros (0A.T1 -> 0A.T2, T09 -> T10),
    // si no, le añade 1 (0A.T -> 0A.T1). Sin hermanas queda vacío.
    nextTaskCode(parentId) {
      const lastTask = this.siblingTasks(parentId).reduce((last, task) => {
        return !last || (Number(task.order_column) || 0) >= (Number(last.order_column) || 0) ? task : last
      }, null)

      const lastCode = lastTask && lastTask.code ? String(lastTask.code).trim() : ''
      if (!lastCode) {
        return ''
      }

      const match = lastCode.match(/^(.*?)(\d+)$/)
      if (!match) {
        return lastCode + '1'
      }

      const [, prefix, digits] = match
      return prefix + String(Number(digits) + 1).padStart(digits.length, '0')
    },

    // tras el drag and drop: order_column = posición (0, 1, 2...) y guarda solo las tareas que cambiaron.
    // El flujo espera órdenes consecutivos desde 0 (la tarea 0 es la inicial y nextTask busca order_column + 1)
    async reorderTasks() {
      const changedTasks = this.form.tasks.filter((task, index) => Number(task.order_column) !== index)
      if (!changedTasks.length) {
        return
      }

      this.form.tasks.forEach((task, index) => {
        task.order_column = index
      })

      this.isSavingTasksOrder = true
      try {
        // code y name son obligatorios en la edición; el resto de campos no se envía para no modificarlos
        const responses = await Promise.all(changedTasks.map((task) => {
          return axios.post('/api/v2/validations/template/task/' + task.id, {
            code: task.code,
            name: task.name,
            order_column: task.order_column,
          })
        }))

        if (responses.every(({ data }) => data && data.success)) {
          this.suggestNewTaskFields()
          this.$notify.success('success_editing')
        } else if (responses.some(({ data }) => data && data.msg)) {
          this.$notify.error('no_access_permissions')
          this.getTemplateData()
        } else {
          this.$notify.error('error_saving')
          this.getTemplateData()
        }
      } catch (error) {
        this.$notify.error('error_saving')
        // recargamos para volver al orden guardado
        this.getTemplateData()
      } finally {
        this.isSavingTasksOrder = false
      }
    },

    // redirige a pantalla edicion
    editTask(taskId) {
      // this.tasksInEdition.push(taskId) edicion inline en desuso
      this.$router.push({
        name: 'validation-template-task.edit', params: { validation_id: this.templateId, task_id: taskId }
      })
    },


    // guarda cambios de la tarea y la saca del modo edicion
    async sendTaskData(taskId, taskKey) {

      var url = '/api/v2/validations/template/task/' + taskId;

      // Enviamos el form
      const { data } = await axios.post(url, this.form.tasks[taskKey])
      if (data && data.success) {

        // la sacamos del modo edicion
        this.tasksInEdition.splice(this.tasksInEdition.indexOf(taskId),1)

        this.$notify.success('success_editing')

      } else if(data && data.msg){

        this.$notify.error('no_access_permissions')

      } else {
        this.$notify.error('error_saving')
      }
    },


    participantAddedInGroup(user) {
      // console.log('usuario añadido', user)
    },


    // crea nueva tarea en esta plantilla
    async linkTask() {
      if (this.isCreatingTask) {
        return
      }

      const code = String(this.newTask.code || '').trim()
      const name = String(this.newTask.name || '').trim()
      if (!code || !name || !this.newTask.manager_group_id) {
        this.$notify.error('task_required_fields')
        return
      }

      // el backend exige un entero; si se deja vacío o inválido se usa el sugerido
      const orderColumn = Number.isInteger(this.newTask.order_column) && this.newTask.order_column >= 0
        ? this.newTask.order_column
        : this.nextTaskOrder(this.newTask.parent_id)

      this.isCreatingTask = true
      try {
        const { data } = await axios.post("/api/v2/validations/template/task", {
          code,
          name,
          description: this.newTask.description,
          order_column: orderColumn,
          manager_group_id: this.newTask.manager_group_id,
          template_id: this.templateId,
          parent_id: this.newTask.parent_id == 0 ? null : this.newTask.parent_id,
          is_concurrent: this.newTask.is_concurrent, // la tarea padre define si las subtareas son concurrentes (no tienen orden secuencial)
        })

        if (data && data.success) {

          // se mantienen grupo inicial y tarea padre para poder crear varias seguidas
          this.newTask.name = ''
          this.newTask.description = ''
          this.newTask.is_concurrent = false
          // reload (también recalcula el orden y código sugeridos de la siguiente tarea)
          await this.getTemplateData()
          this.$notify.success('success_editing')
          this.$nextTick(() => this.$refs.newTaskName && this.$refs.newTaskName.focus())

        } else if(data && data.msg){

          this.$notify.error('no_access_permissions')

        } else {
          this.$notify.error('error_saving')
        }
      } catch (error) {
        this.$notify.error('error_saving')
      } finally {
        this.isCreatingTask = false
      }
    },


    // elimina la task de esta plantilla
    async unlinkTask(taskId, taskName) {

      let self = this;
      this.$dialog.confirm({
        title: this.$t('delete'),
        message: this.$t('delete_generic', [taskName]),
        confirmText: this.$t('delete'),
        type: 'is-danger',
        hasIcon: true,
        onConfirm: async () => {

          const { data } = await axios.post("/api/v2/validations/template/task/" + taskId + "/delete", {})
          if (data && data.success) {

            // reload
            this.getTemplateData()
            this.$notify.success('success_deleting_generic')

          } else if(data && data.msg){

            this.$notify.error('no_access_permissions')

          } else {
            this.$notify.error('error_saving')
          }
        }
      })
    },


    // vincula el grupo seleccionado con la plantilla
    async linkGroup() {
      if (!this.groupToAdd.id) {
        this.$notify.error('Debe seleccionar un grupo de la lista')
        return
      }

      // console.log("añadir el grupo ", this.groupToAdd)
      const { data } = await axios.post("/api/v2/validations/template/group", {
        company_id: this.company.id,
        template_id: this.templateId,
        group_code: this.groupToAdd.code,
      })
      if (data && data.success) {

        // reload
        this.getTemplateData()
        this.groupToAdd = {}
        this.$notify.success('success_editing')

      } else if(data && data.msg){

        this.$notify.error('no_access_permissions')

      } else {
        this.$notify.error('error_saving')
      }
    },


    // desvincula el grupo de esta plantilla
    async unlinkGroup(groupId, groupName) {

      let self = this;
      this.$dialog.confirm({
        title: this.$t('delete'),
        message: this.$t('delete_generic', [groupName]),
        confirmText: this.$t('delete'),
        type: 'is-danger',
        hasIcon: true,
        onConfirm: async () => {

          const { data } = await axios.post("/api/v2/validations/template/group/" + groupId + "/delete", { })
          if (data && data.success) {
            // reload
            this.getTemplateData()

            this.$notify.success('success_deleting_generic')

          } else if(data && data.msg){

            this.$notify.error('no_access_permissions')

          } else {
            let msg = 'error_saving'
            if( data && data.error )
              msg = data.error

            this.$notify.error(msg)
          }
        }
      })
    },


    disableButton() {
      this.disableSaveButton = true
      let self = this
      setTimeout(function() {
        self.disableSaveButton = false
      }, 2000);
    }

  },
};
</script>
